/**
 * Hook for high-quality STL export compilation.
 *
 * This uses a completely separate Web Worker instance from the live preview
 * worker so a high-quality re-compile never blocks or disrupts the 3D viewer.
 *
 * Usage:
 *   const { compileForExport, isExporting, exportProgress } = useHighQualityExport();
 *   const result = await compileForExport(scadCode, parameters, projectFiles, 4);
 *   if (result) await downloadSTLFile(result.blob, undefined, result.coloredBlobs, parameters);
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import {
  WorkerMessage,
  WorkerMessageType,
  WorkerProgressData,
  ColoredSTL,
  VirtualFile,
} from '@/worker/types';
import { type WorkerFile } from '@/hooks/useOpenSCAD';
import { type ColoredOutput } from '@/hooks/useOpenSCAD';
import { injectExportFn, type ExportQuality } from '@/utils/exportQualityUtils';
import {
  prepareSvgFragments,
  makeWorkerFileSvgResolver,
  type SvgFragmentFile,
} from '@/utils/svgFragmentPrepare';

export type HighQualityExportResult = {
  blob: Blob;
  coloredBlobs: ColoredOutput[];
};

type PendingResolve = (result: HighQualityExportResult | null) => void;

export function useHighQualityExport() {
  const [isExporting, setIsExporting] = useState(false);
  const [exportProgress, setExportProgress] =
    useState<WorkerProgressData | null>(null);

  const workerRef = useRef<Worker | null>(null);
  /** Resolve function for the currently in-flight compile Promise */
  const pendingResolveRef = useRef<PendingResolve | null>(null);

  const handleMessage = useCallback((event: MessageEvent) => {
    const { type, data } = event.data;

    if (type === WorkerMessageType.PROGRESS) {
      setExportProgress(data as WorkerProgressData);
      return;
    }

    // During multi-color compilation the worker streams PARTIAL_RESULT
    // messages (one per color). For export we only care about the final
    // complete result, so skip these entirely.
    if (type === WorkerMessageType.PARTIAL_RESULT) {
      return;
    }

    // Also skip FS_WRITE_BATCH acknowledgements (not relevant here).
    if (type === WorkerMessageType.FS_WRITE_BATCH) {
      return;
    }

    const resolve = pendingResolveRef.current;
    pendingResolveRef.current = null;
    setIsExporting(false);
    setExportProgress(null);

    if (!resolve) return;

    if (event.data.err) {
      console.error('[HQExport] Compile error:', event.data.err);
      resolve(null);
      return;
    }

    if (data && (data as any).output) {
      const blob = new Blob([(data as any).output], { type: 'model/stl' });

      const coloredBlobs: ColoredOutput[] =
        (data as any).coloredOutputs?.map((c: ColoredSTL) => ({
          color: c.color,
          blob: new Blob([c.data], { type: 'model/stl' }),
          isGhost: c.isGhost,
          excludedFromExport: c.excludedFromExport,
          material: c.material,
        })) ?? [];

      resolve({ blob, coloredBlobs });
    } else {
      resolve(null);
    }
  }, []);

  // Create the dedicated worker once on mount, destroy on unmount.
  useEffect(() => {
    const worker = new Worker(new URL('../worker/worker.ts', import.meta.url), {
      type: 'module',
    });
    worker.addEventListener('message', handleMessage);
    workerRef.current = worker;

    return () => {
      worker.terminate();
      workerRef.current = null;
      // Resolve any pending promise so callers don't hang
      if (pendingResolveRef.current) {
        pendingResolveRef.current(null);
        pendingResolveRef.current = null;
      }
    };
  }, [handleMessage]);

  /**
   * Sync project files to the export worker's virtual filesystem.
   */
  const syncFiles = useCallback(async (files: WorkerFile[]) => {
    const worker = workerRef.current;
    if (!worker || files.length === 0) return;

    const virtualFiles: VirtualFile[] = files.map((f) => ({
      path: f.filename,
      content: f.content,
      isBinary: f.isBinary,
    }));

    const message: WorkerMessage = {
      type: WorkerMessageType.FS_WRITE_BATCH,
      data: { files: virtualFiles },
    };
    worker.postMessage(message);

    // Small delay to ensure FS writes complete before compiling
    await new Promise((resolve) => setTimeout(resolve, 50));
  }, []);

  /**
   * Re-compile the model at the given quality multiplier and return blobs.
   *
   * - At 1x the caller should just use the blob already in BlobContext; this
   *   function will still work but wastes a compile.
   * - The $fn value from the source code is multiplied by `multiplier` and
   *   injected as a -D$fn=<value> override flag so no source modification is
   *   needed.
   * - Returns null if compilation fails.
   */
  const compileForExport = useCallback(
    async (
      code: string,
      projectFiles: WorkerFile[],
      multiplier: ExportQuality,
    ): Promise<HighQualityExportResult | null> => {
      const worker = workerRef.current;
      if (!worker) return null;

      setIsExporting(true);
      setExportProgress({ progress: 0, message: 'Starting...' });

      // Sync project files first
      if (projectFiles.length > 0) {
        await syncFiles(projectFiles);
      }

      // Build the export code with the scaled $fn injected in-place.
      // OpenSCAD's -D flag does not support special variables starting with $,
      // so we replace the existing $fn assignment directly in the source.
      const exportCode = injectExportFn(code, multiplier);

      // Pre-parse SVG partial-imports on the main thread.  DOMParser is not
      // universally available in WorkerGlobalScope, so we synthesize the
      // fragment SVGs here and ship them to the worker as part of the
      // PREVIEW message.  The rewritten code points at the virtual paths.
      let preparedCode = exportCode;
      let svgFragmentFiles: SvgFragmentFile[] = [];
      try {
        const prep = await prepareSvgFragments(
          exportCode,
          makeWorkerFileSvgResolver(projectFiles),
        );
        preparedCode = prep.rewrittenCode;
        svgFragmentFiles = prep.fragmentFiles;
      } catch (err) {
        console.error('[HQExport] SVG fragment pre-parse failed:', err);
        setIsExporting(false);
        setExportProgress(null);
        return null;
      }

      // Pass an empty params array — all parameter values (width, height,
      // colors, fonts, etc.) are already baked into the code string by the
      // editor's updateParameter() flow.  Passing them as -D flags would be
      // redundant and causes parse errors for complex types (colors, strings,
      // fonts).  This matches the normal preview path in useOpenSCAD which
      // also sends params: [].
      return new Promise<HighQualityExportResult | null>((resolve) => {
        pendingResolveRef.current = resolve;

        const message: WorkerMessage = {
          type: WorkerMessageType.PREVIEW,
          data: {
            code: preparedCode,
            params: [],
            fileType: 'stl',
            svgFragmentFiles,
          },
        };
        worker.postMessage(message);
      });
    },
    [syncFiles],
  );

  return {
    compileForExport,
    isExporting,
    exportProgress,
  };
}
